import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';

interface Contato {
  id: string;
  nome: string;
  email: string;
  telefone_whatsapp: string | null;
  mensagem: string;
  created_at: string;
}

export default function ContactsTable() {
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    const fetchContatos = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('contatos')
        .select('id, nome, email, telefone_whatsapp, mensagem, created_at')
        .order('created_at', { ascending: false });

      if (!error && data) setContatos(data);
      setLoading(false);
    };
    fetchContatos();
  }, []);

  const filtered = contatos.filter((c) => {
    const q = search.toLowerCase();
    return (
      c.nome.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.telefone_whatsapp ?? '').toLowerCase().includes(q)
    );
  });

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif font-bold text-xl" style={{ color: '#0B2F5B' }}>
            Mensagens de Contato
          </h2>
          <p className="text-xs mt-0.5" style={{ color: '#1A1A1A', opacity: 0.45 }}>
            {contatos.length} mensagem{contatos.length !== 1 ? 's' : ''} recebida{contatos.length !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-64">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
            <i className="ri-search-line text-sm" style={{ color: '#0B2F5B', opacity: 0.4 }} />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou e-mail..."
            className="w-full h-10 pl-9 pr-4 rounded-xl text-sm outline-none transition-all duration-200"
            style={{ background: '#F8F9FD', border: '2px solid rgba(11,47,91,0.1)', color: '#1A1A1A' }}
            onFocus={(e) => { e.target.style.borderColor = '#C9A24A'; }}
            onBlur={(e) => { e.target.style.borderColor = 'rgba(11,47,91,0.1)'; }}
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div
            className="w-10 h-10 rounded-full border-4 border-t-transparent animate-spin"
            style={{ borderColor: '#C9A24A', borderTopColor: 'transparent' }}
          />
        </div>
      )}

      {/* Empty */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-20">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(11,47,91,0.06)' }}>
            <i className="ri-inbox-line text-2xl" style={{ color: '#0B2F5B', opacity: 0.3 }} />
          </div>
          <p className="text-sm font-medium" style={{ color: '#0B2F5B', opacity: 0.4 }}>
            {search ? 'Nenhum resultado encontrado.' : 'Nenhuma mensagem recebida ainda.'}
          </p>
        </div>
      )}

      {/* Table */}
      {!loading && filtered.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(11,47,91,0.1)' }}>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'rgba(11,47,91,0.04)', borderBottom: '1px solid rgba(11,47,91,0.08)' }}>
                  <th className="text-left px-5 py-3 text-xs font-semibold" style={{ color: '#0B2F5B', opacity: 0.6 }}>Nome</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold" style={{ color: '#0B2F5B', opacity: 0.6 }}>E-mail</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold" style={{ color: '#0B2F5B', opacity: 0.6 }}>Telefone</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold" style={{ color: '#0B2F5B', opacity: 0.6 }}>Data</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold" style={{ color: '#0B2F5B', opacity: 0.6 }}>Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <>
                    <tr
                      key={c.id}
                      onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                      className="cursor-pointer transition-colors duration-150 hover:bg-amber-50"
                      style={{
                        borderBottom: i < filtered.length - 1 ? '1px solid rgba(11,47,91,0.06)' : 'none',
                        background: expanded === c.id ? 'rgba(201,162,74,0.05)' : undefined,
                      }}
                    >
                      <td className="px-5 py-4 font-semibold" style={{ color: '#0B2F5B' }}>{c.nome}</td>
                      <td className="px-5 py-4" style={{ color: '#1A1A1A', opacity: 0.7 }}>
                        <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="hover:underline" style={{ color: '#C9A24A' }}>
                          {c.email}
                        </a>
                      </td>
                      <td className="px-5 py-4" style={{ color: '#1A1A1A', opacity: 0.7 }}>
                        {c.telefone_whatsapp ? (
                          <a href={`tel:${c.telefone_whatsapp}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
                            {c.telefone_whatsapp}
                          </a>
                        ) : (
                          <span style={{ opacity: 0.35 }}>—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-xs whitespace-nowrap" style={{ color: '#1A1A1A', opacity: 0.5 }}>
                        {formatDate(c.created_at)}
                      </td>
                      <td className="px-5 py-4 max-w-xs">
                        <p className="text-xs truncate" style={{ color: '#1A1A1A', opacity: 0.6 }}>{c.mensagem}</p>
                      </td>
                    </tr>
                    {expanded === c.id && (
                      <tr key={`${c.id}-expanded`} style={{ background: 'rgba(201,162,74,0.04)', borderBottom: i < filtered.length - 1 ? '1px solid rgba(11,47,91,0.06)' : 'none' }}>
                        <td colSpan={5} className="px-5 py-4">
                          <div className="rounded-xl p-4" style={{ background: 'rgba(11,47,91,0.04)' }}>
                            <p className="text-xs font-semibold mb-2" style={{ color: '#0B2F5B', opacity: 0.5 }}>MENSAGEM COMPLETA</p>
                            <p className="text-sm leading-relaxed" style={{ color: '#1A1A1A' }}>{c.mensagem}</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden flex flex-col divide-y" style={{ borderColor: 'rgba(11,47,91,0.08)' }}>
            {filtered.map((c) => (
              <div
                key={c.id}
                className="p-4 cursor-pointer transition-colors duration-150"
                style={{ background: expanded === c.id ? 'rgba(201,162,74,0.04)' : '#FFFFFF' }}
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="font-semibold text-sm" style={{ color: '#0B2F5B' }}>{c.nome}</p>
                  <span className="text-xs shrink-0" style={{ color: '#1A1A1A', opacity: 0.4 }}>{formatDate(c.created_at)}</span>
                </div>
                <p className="text-xs mb-1" style={{ color: '#C9A24A' }}>{c.email}</p>
                {c.telefone_whatsapp && <p className="text-xs mb-2" style={{ color: '#1A1A1A', opacity: 0.55 }}>{c.telefone_whatsapp}</p>}
                {expanded === c.id ? (
                  <p className="text-xs leading-relaxed mt-2" style={{ color: '#1A1A1A', opacity: 0.7 }}>{c.mensagem}</p>
                ) : (
                  <p className="text-xs truncate" style={{ color: '#1A1A1A', opacity: 0.5 }}>{c.mensagem}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
